import { motion } from "framer-motion";
import { CalendarCheck, Phone, Clock, MapPin, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
      <div className="pointer-events-none absolute -left-32 top-16 h-96 w-96 rounded-full bg-brand-blush blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-brand-green/15 blur-3xl" />

      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-20 pt-14 lg:grid-cols-12 lg:px-10 lg:pb-28 lg:pt-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-6"
        >
          <span
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 rounded-full border border-brand-green/40 bg-brand-green/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-brand-greendark"
          >
            <BadgeCheck className="h-3.5 w-3.5" /> Your local Priceline Pharmacy
          </span>
          <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-heading">
            Caring for
            <span className="text-brand-pink"> Margaret River's</span> health, every day.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg" data-testid="hero-subtext">
            From vaccinations and compounding to skin, wound and women's health consults — plus our own
            pharmacist-formulated <span className="font-serif italic">DermaGen</span> skincare, born right here in our
            compounding room.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Button
              asChild
              size="lg"
              data-testid="hero-book-service-btn"
              className="rounded-full bg-brand-pink px-7 font-heading text-base font-semibold text-white shadow-[0_8px_24px_rgba(229,0,126,0.3)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-brand-pinkdark"
            >
              <a href="#book">
                <CalendarCheck className="mr-2 h-5 w-5" /> Book a service
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              data-testid="hero-call-btn"
              className="rounded-full border-2 border-foreground/15 bg-white/70 px-7 font-heading text-base font-semibold transition-transform duration-200 hover:-translate-y-0.5 hover:border-brand-pink hover:text-brand-pink"
            >
              <a href="tel:+61897572224">
                <Phone className="mr-2 h-5 w-5" /> 9757 2224
              </a>
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-muted-foreground" data-testid="hero-quickfacts">
            <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-brand-green" /> Mon–Sun, 8am–6pm</span>
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-brand-green" /> Margaret River, WA</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.15 }}
          className="relative lg:col-span-6"
        >
          <div className="absolute -bottom-5 -left-5 h-full w-full rounded-[2rem] bg-brand-pink/10" />
          <img
            src="/assets/pharmacy-storefront.jpg"
            alt="Priceline Pharmacy Margaret River storefront"
            data-testid="hero-storefront-image"
            className="relative max-h-[62vh] w-full rounded-[2rem] object-cover shadow-[0_24px_64px_rgba(0,0,0,0.14)]"
          />
          <div
            data-testid="hero-hours-card"
            className="absolute -bottom-6 right-6 rounded-2xl border border-white/40 bg-white/80 px-5 py-4 shadow-[0_12px_32px_rgba(0,0,0,0.1)] backdrop-blur-xl"
          >
            <p className="font-heading text-sm font-bold">Open today</p>
            <p className="text-sm text-muted-foreground">8:00am – 6:00pm</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
